<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" size="large" :isClosable="false" :isCloseButtonVisible="!isSaving">
    <template #content>
      <div
        :style="{
          opacity: isSaving ? '0.5' : '1',
          pointerEvents: isSaving ? 'none' : 'auto',
        }"
      >
        <div style="margin-bottom: 16px; margin-left: -5px">
          <SrpSegmentedControl v-model="mode" :options="modeOptions" size="md" aria-label="Post creation mode" />
        </div>

        <div style="padding-bottom: 12px; border-bottom: 1px solid #e0e0e0">
          <div class="create-type-row">
            <h2 class="create-type-row__label">Create</h2>
            <SrpSegmentedControl v-model="selectedCreateType" :options="createTypeSegmentOptions" size="md" aria-label="Post type" @change="handleCreateTypeChange" />
          </div>
          <p v-if="showAiPostOnlyWarning" class="create-type-row__ai-warning">AI assisted only supports Posts currently.</p>
        </div>

        <div style="min-height: 300px">
          <!-- AI Assisted Mode -->
          <div v-if="mode === 'ai'">
            <NoteWithIcon v-if="isManyPosts" color="green" size="tiny" class="many-posts-instructions">
              <template #text>
                <p class="global-p" style="margin: 0">
                  Create a series of up to {{ maxCreateManyPosts }} posts around a theme or goal.
                  <br />
                  Describe what to post, how often, and for how long.
                </p>
              </template>
            </NoteWithIcon>
            <div v-if="isManyPosts" class="quick-starts">
              <span class="quick-starts__label">Quick starts</span>
              <button
                v-for="quickStart in manyPostsQuickStarts"
                :key="quickStart.id"
                type="button"
                :class="{ 'quick-starts__pill': true, 'quick-starts__pill--selected': selectedQuickStartId === quickStart.id }"
                @click="applyQuickStart(quickStart)"
              >
                {{ quickStart.label }}
              </button>
              <SrpDropdown placement="bottom-end" :offset="4">
                <template #clickableElement="{ isDropdownVisible }">
                  <button
                    type="button"
                    class="quick-starts__pill quick-starts__pill--more"
                    :class="{ 'quick-starts__pill--selected': isMoreQuickStartSelected }"
                    aria-haspopup="menu"
                    :aria-expanded="isDropdownVisible"
                  >
                    more
                    <IconEmbedded :name="isDropdownVisible ? 'caret-top_3' : 'caret-bottom_3'" :size="11" color="#666" />
                  </button>
                </template>
                <template #dropdown="{ close }">
                  <div class="quick-starts__more-menu" role="menu">
                    <button
                      v-for="quickStart in moreQuickStarts"
                      :key="quickStart.id"
                      type="button"
                      role="menuitem"
                      class="quick-starts__more-item"
                      :class="{ 'quick-starts__more-item--selected': selectedQuickStartId === quickStart.id }"
                      @click="applyMoreQuickStart(quickStart, close)"
                    >
                      {{ quickStart.label }}
                    </button>
                  </div>
                </template>
              </SrpDropdown>
            </div>
            <div style="margin-top: 20px">
              <label class="input-label">Theme or Goal</label>
              <input
                :class="{
                  'global-text-input': true,
                  'global-text-input--error': !isFormValid && !_title,
                }"
                v-model="_title"
                style="max-width: 300px"
                :placeholder="isManyPosts ? 'e.g. Restaurant Mondays' : 'e.g. festival, local food...'"
                type="text"
                @focus="isFormValid = true"
              />
            </div>

            <div style="margin-top: 16px">
              <label class="input-label">{{ isManyPosts ? "Describe your post series" : "Describe your post" }}</label>
              <textarea
                :class="{
                  'global-textarea': true,
                  'global-textarea--error': !isFormValid && !_description,
                }"
                v-model="_description"
                style="height: 85px; min-height: 85px"
                :placeholder="isManyPosts ? 'Highlight a local restaurant every Monday for the next 6 weeks.' : 'Describe the key elements to include, style, etc.'"
                @focus="isFormValid = true"
              ></textarea>
            </div>

            <div style="margin-top: 20px">
              <h4 style="margin-bottom: 8px">{{ isManyPosts ? "Content Album (optional)" : "Content Album" }}</h4>
              <div v-if="!selectedContentSummary">
                {{ isManyPosts ? "We'll auto-match a content album, or you can pick manually." : "We'll find a content album to match with, or" }}<br />
                <LinkWithIcon isDottedUnderline @click="isPickContentModalVisible = true" style="margin-top: 8px">
                  <template #icon>
                    <IconEmbedded name="folder_2" :size="19" />
                  </template>
                  <span>Manually choose</span>
                </LinkWithIcon>
              </div>
              <div v-else style="margin-top: 8px">
                <ContentSummary
                  :contentSummaryData="selectedContentSummary"
                  :isContentSourcedFromLabelVisible="false"
                  :hideDecorations="true"
                  :customerId="props.customerId"
                  @clickViewPhotos="handleViewPhotos"
                />
              </div>
            </div>
          </div>

          <!-- Manual Mode -->
          <div v-if="mode === 'manual'">
            <div v-if="!isStoryPost" style="margin-top: 20px">
              <label class="input-label">Caption</label>
              <PostCaptionEditorPanel
                v-model:caption="_caption"
                :customerId="props.customerId"
                :assetIds="manualSelectedAssetIds"
                aiLinkColor="gray"
                :hasError="!isFormValid && isCaptionRequired && !_caption"
                placeholder="Write your post caption..."
                @focus="isFormValid = true"
              />
            </div>

            <div style="margin-top: 20px">
              <label class="input-label">{{ mediaSectionLabel }}</label>

              <div
                :class="{
                  'photo-section-container': true,
                  'photo-section-container--error': !isFormValid && manualSelectedAssetIds.length === 0,
                }"
                @click="isFormValid = true"
              >
                <PostMediaSelectionPanel
                  v-model:selectedAssetIds="manualSelectedAssetIds"
                  v-model:uploadedAssetIds="uploadedAssetIds"
                  :customerId="props.customerId"
                  :postType="postType"
                  :displayedAssetIds="displayedManualAlbumAssetIds"
                  inputId="new-post-manual-upload"
                  galleryPostfix="manualAlbumSelection"
                  :isLoadingAlbum="isLoadingManualAlbum"
                  :showThumbnailGrid="Boolean(manualAlbumPayload)"
                  :albumPickerLabel="manualAlbumPayload ? 'Change Album' : 'Select from an Album'"
                  :hasAlbum="Boolean(manualAlbumPayload)"
                  @openAlbumPicker="isManualAlbumPickerOpen = true"
                  @selectionChanged="handleMediaSelectionChanged"
                  @uploadSelected="handlePhotoUploadSelect"
                  @uploadDeselected="handlePhotoUploadDeselect"
                />
              </div>

              <div v-if="!isFormValid && manualValidationErrorMessage" class="global-error-string" style="margin-top: 8px">{{ manualValidationErrorMessage }}</div>
            </div>

            <PostTaggingPanel
              v-model:locationTagSettings="locationTagSettings"
              v-model:physicalLocationId="physicalLocationId"
              v-model:instagramCollaboratorHandles="instagramCollaboratorHandles"
              :customerId="props.customerId"
              :postType="postType"
              :assetIds="manualSelectedAssetIds"
              :selectedChannels="props.selectedChannels"
              :isDisabled="isSaving"
              :collapseKey="taggingPanelResetKey"
            />
          </div>
        </div>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex">
        <SrpButton
          fill="outlined"
          color="gray"
          @click="
            () => {
              emit('cancel');
              closeModal();
            }
          "
          :isDisabled="isSaving"
        >
          Cancel
        </SrpButton>

        <SrpButton
          @click="
            async () => {
              const success = await save();
              if (success) {
                closeModal();
              }
            }
          "
          :isDisabled="isSaving"
        >
          <template #icon>
            <LoadingIcon v-if="isSaving" :size="20" />
            <IconEmbedded v-else :name="mode === 'manual' ? 'save_2-5' : 'ai_2'" :size="20" />
          </template>
          {{ buttonText }}
        </SrpButton>
      </div>
    </template>
  </SrpModal>

  <PickFromExistingContentModal
    v-if="isPickContentModalVisible"
    v-model:isVisible="isPickContentModalVisible"
    :customerId="props.customerId"
    :calendarItem="null"
    @close="isPickContentModalVisible = false"
    @picked="contentSummary => handleContentSummarySelected(contentSummary)"
  />

  <ViewAllPhotosModal v-if="isViewAllPhotosModalVisible" v-model:isVisible="isViewAllPhotosModalVisible" :customerId="props.customerId" :contentSummaryId="viewAllPhotosSummaryId" />

  <PickFromExistingContentModal
    v-if="isManualAlbumPickerOpen"
    v-model:isVisible="isManualAlbumPickerOpen"
    :customerId="props.customerId"
    :calendarItem="null"
    :videoOnly="videoOnlyForAlbumPicker"
    :showPhotoAndVideoCount="showAlbumPhotoAndVideoCount"
    @close="isManualAlbumPickerOpen = false"
    @picked="handleManualAlbumSelected"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import SrpSegmentedControl from "@components/ui/SrpSegmentedControl.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import ContentSummary from "@views/Community/ContentCalendar/eventSnippets/ContentSummary.vue";
import ViewAllPhotosModal from "@views/Community/ContentCalendar/steps/modals/ViewAllPhotosModal.vue";
import PostMediaSelectionPanel from "@components/SocialPostEditAndShareModal/PostMediaSelectionPanel.vue";
import PostCaptionEditorPanel from "@components/SocialPostEditAndShareModal/PostCaptionEditorPanel.vue";
import PostTaggingPanel from "@components/SocialLocations/PostTaggingPanel.vue";

// Types
import { ContentCalendarItem, ContentCalendarItemMatch, ContentSummaryPreview, SocialPostType } from "@contracts/contentCalendar";
import type { IconName } from "@contracts/IconName";
import { SocialShareSummaryPayload, SocialShareSummaryContentType } from "@contracts/shareStop";
import type { SocialPostLocationTagSettingsData } from "@contracts/socialLocations";
import type { SocialChannels } from "@contracts/socialNetworks";

// Repos
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Constants
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import FileUtils from "@logic/FileUtils";
import { filterAlbumAssetsForPostType, isCaptionRequiredForPostType, validateManualPostInputs, validateManualPostMediaMetadata } from "@logic/AtlasSchedulingValidation";
import { usePostTypeMediaRules } from "@composables/usePostTypeMediaRules";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string;
    weekGroup?: string | null;
    /** When opening create flow, start in this mode ("ai" | "manual"). */
    initialMode?: "ai" | "manual" | null;
    initialTitle?: string | null;
    initialDescription?: string | null;
    /** Prefill manual mode caption when opening create flow. */
    initialCaption?: string | null;
    /** Pre-select post type when opening create flow (e.g. Reel or Story from calendar menu). */
    initialPostType?: SocialPostType | null;
    selectedChannels?: SocialChannels[];
    enableCreateManyPosts?: boolean;
    maxCreateManyPosts?: number;
  }>(),
  {
    weekGroup: null,
    initialMode: null,
    initialTitle: null,
    initialDescription: null,
    initialCaption: null,
    initialPostType: null,
    selectedChannels: () => [],
    enableCreateManyPosts: false,
    maxCreateManyPosts: 8,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "createCalendarItems", items: ContentCalendarItem[]): void;
  (e: "createNewItemAndPost", newItemAndPost: ContentCalendarItemMatch): void;
  (e: "cancel"): void;
}>();

// Local values ===============================================================
const _title = ref<string>("");
const _description = ref<string>("");

// Mode toggle ================================================================
const mode = ref<string>("ai");
const MANY_POSTS_CREATE_TYPE = "ManyPosts" as const;
type CreateType = SocialPostType | typeof MANY_POSTS_CREATE_TYPE;
const selectedCreateType = ref<CreateType>(SocialPostType.Post);
const postType = computed<SocialPostType>(() =>
  selectedCreateType.value === SocialPostType.Reel || selectedCreateType.value === SocialPostType.Story ? selectedCreateType.value : SocialPostType.Post
);
const isManyPosts = computed(() => selectedCreateType.value === MANY_POSTS_CREATE_TYPE);

type ManyPostsQuickStart = {
  id: string;
  label: string;
  title: string;
  description: string;
};

const manyPostsQuickStarts: ManyPostsQuickStart[] = [
  {
    id: "food-and-drink",
    label: "Food & Drink",
    title: "Local Food and Drink",
    description: "Highlight a different restaurant, café, brewery, winery, or other food and drink experience.\nCreate 1 post each week for 4 weeks.",
  },
  {
    id: "shop-local",
    label: "Shop Local",
    title: "Local Shopping",
    description: "Highlight a different local shop or shopping experience.\nCreate 3 posts next week, spread throughout the week.",
  },
  {
    id: "arts-and-culture",
    label: "Arts & Culture",
    title: "Arts and Culture",
    description: "Feature museums, galleries, public art, performances, and other cultural experiences.\nCreate a post each Monday next month.",
  },
  {
    id: "outdoor-experiences",
    label: "Outdoor Experiences",
    title: "Explore the Outdoors",
    description: "Highlight a different park, trail, waterway, or outdoor activity.\nCreate 1 post each week for 5 weeks.",
  },
];

const moreQuickStarts: ManyPostsQuickStart[] = [
  {
    id: "local-eats",
    label: "Local Eats",
    title: "Local Eats",
    description: "Highlight a different locally owned restaurant, café, bakery, or other place to eat.\nCreate a post each Friday for the next 6 weeks.",
  },
  {
    id: "local-business-spotlight",
    label: "Local Business Spotlight",
    title: "Meet Our Local Businesses",
    description: "Highlight a different local business or organization in each post.\nCreate 1 post every other week for the next 6 weeks, starting next week.",
  },
  {
    id: "seasonal-activities",
    label: "Seasonal Activities",
    title: "Seasonal Activities",
    description: "Feature activities, attractions, and experiences relevant to the season.\nCreate 4 posts next month, spread throughout the month.",
  },
  {
    id: "destination-highlights",
    label: "Destination Highlights",
    title: "Explore Our Destination",
    description: "Highlight different attractions, activities, and experiences across our destination.\nCreate 1 post each week for 6 weeks.",
  },
];

const selectedQuickStartId = ref<string | null>(null);
const isApplyingQuickStart = ref(false);
const isMoreQuickStartSelected = computed(() => moreQuickStarts.some(quickStart => quickStart.id === selectedQuickStartId.value));

function applyQuickStart(quickStart: ManyPostsQuickStart) {
  isApplyingQuickStart.value = true;
  _title.value = quickStart.title;
  _description.value = quickStart.description;
  selectedQuickStartId.value = quickStart.id;
  isFormValid.value = true;
  nextTick(() => {
    isApplyingQuickStart.value = false;
  });
}

function applyMoreQuickStart(quickStart: ManyPostsQuickStart, close: () => void) {
  applyQuickStart(quickStart);
  close();
}
const postTypeIconMap: Record<SocialPostType, IconName> = {
  [SocialPostType.Post]: "social-post",
  [SocialPostType.Reel]: "social-reel",
  [SocialPostType.Story]: "social-story",
};
const modeOptions = computed(() => [
  { value: "ai", label: "AI Assisted", icon: "ai_2" as const, disabled: postType.value !== SocialPostType.Post },
  { value: "manual", label: "Manual Mode", icon: "edit-pencil_2" as const, disabled: isManyPosts.value },
]);
const createTypeSegmentOptions = computed(() => [
  { value: SocialPostType.Post, label: "One Post", icon: postTypeIconMap[SocialPostType.Post] },
  ...(props.enableCreateManyPosts ? [{ value: MANY_POSTS_CREATE_TYPE, label: "Multiple Posts", icon: "copy_2" as IconName }] : []),
  { value: SocialPostType.Reel, label: SocialPostType.Reel, icon: postTypeIconMap[SocialPostType.Reel] },
  { value: SocialPostType.Story, label: SocialPostType.Story, icon: postTypeIconMap[SocialPostType.Story] },
]);
const showAiPostOnlyWarning = computed(() => postType.value === SocialPostType.Reel || postType.value === SocialPostType.Story);

function handleCreateTypeChange({ newValue, oldValue }: { newValue: string | number; oldValue: string | number }) {
  const newType = newValue as CreateType;
  const oldType = oldValue as CreateType;
  if (newType === MANY_POSTS_CREATE_TYPE) {
    mode.value = "ai";
    return;
  }
  if (newType === SocialPostType.Reel || newType === SocialPostType.Story) {
    mode.value = "manual";
  } else if (newType === SocialPostType.Post && (oldType === SocialPostType.Reel || oldType === SocialPostType.Story)) {
    mode.value = "ai";
  }
}

const isStoryPost = computed(() => postType.value === SocialPostType.Story);
const { mediaSectionLabel, videoOnlyForAlbumPicker, showAlbumPhotoAndVideoCount } = usePostTypeMediaRules(postType);

const isCaptionRequired = computed(() => isCaptionRequiredForPostType(postType.value));

const manualValidationError = ref<string>("");
const manualValidationErrorMessage = computed(() => {
  if (isFormValid.value || mode.value !== "manual") {
    return "";
  }
  if (manualValidationError.value) {
    return manualValidationError.value;
  }
  const result = validateManualPostInputs({
    postType: postType.value,
    assetIds: manualSelectedAssetIds.value,
    caption: _caption.value,
  });
  return result.ok ? "" : result.message;
});

watch(selectedCreateType, () => {
  const newType = postType.value;
  if (newType !== SocialPostType.Post && mode.value === "ai") {
    mode.value = "manual";
  }
  if (newType === SocialPostType.Reel) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(assetId => FileUtils.isVideoFileType(assetId)).slice(0, 1);
  } else if (newType === SocialPostType.Post) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(assetId => !FileUtils.isVideoFileType(assetId));
  } else if (newType === SocialPostType.Story) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.slice(0, 1);
  }
  locationTagSettings.value = [];
  physicalLocationId.value = null;
  if (newType === SocialPostType.Story) {
    instagramCollaboratorHandles.value = [];
  }
  if (manualContentSummaryPreview.value) {
    loadManualAlbumPayload(manualContentSummaryPreview.value);
  }
  manualValidationError.value = "";
  isFormValid.value = true;
});

watch(mode, newMode => {
  if (newMode === "manual" && isManyPosts.value) {
    selectedCreateType.value = SocialPostType.Post;
  }
  if (newMode === "ai" && postType.value !== SocialPostType.Post) {
    mode.value = "manual";
  }
});

// Content group selection ====================================================
const selectedContentSummary = ref<ContentSummaryPreview | null>(null);
const isPickContentModalVisible = ref<boolean>(false);

function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  selectedContentSummary.value = contentSummary;
  isPickContentModalVisible.value = false;
}

// View all photos modal ======================================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string>("");

function handleViewPhotos() {
  viewAllPhotosSummaryId.value = selectedContentSummary.value?.id || "";
  isViewAllPhotosModalVisible.value = true;
}

// Manual mode state ===========================================================
const _caption = ref<string>("");
const manualSelectedAssetIds = ref<string[]>([]);
const locationTagSettings = ref<SocialPostLocationTagSettingsData[]>([]);
const physicalLocationId = ref<string | null>(null);
const instagramCollaboratorHandles = ref<string[]>([]);
const taggingPanelResetKey = ref(0);
const uploadedAssetIds = ref<string[]>([]);
const isManualAlbumPickerOpen = ref<boolean>(false);
const manualAlbumPayload = ref<SocialShareSummaryPayload | null>(null);
const manualContentSummaryPreview = ref<ContentSummaryPreview | null>(null);
const manualUploadsSelectedCount = ref<number>(0);
const isLoadingManualAlbum = ref<boolean>(false);

/** Declared before resetCreateFormState / immediate watch so setup does not hit the TDZ. */
const isFormValid = ref<boolean>(true);

function resetCreateFormState() {
  selectedCreateType.value = props.initialPostType ?? SocialPostType.Post;
  mode.value = props.initialMode ?? "ai";
  selectedQuickStartId.value = null;
  _title.value = props.initialTitle ?? "";
  _description.value = props.initialDescription ?? "";
  _caption.value = props.initialCaption ?? "";
  manualSelectedAssetIds.value = [];
  locationTagSettings.value = [];
  physicalLocationId.value = null;
  instagramCollaboratorHandles.value = [];
  taggingPanelResetKey.value++;
  uploadedAssetIds.value = [];
  manualUploadsSelectedCount.value = 0;
  isManualAlbumPickerOpen.value = false;
  manualAlbumPayload.value = null;
  manualContentSummaryPreview.value = null;
  selectedContentSummary.value = null;
  isPickContentModalVisible.value = false;
  manualValidationError.value = "";
  isFormValid.value = true;
}

function applyFormStateForOpenModal() {
  if (!props.isVisible) {
    return;
  }
  resetCreateFormState();
}

watch(
  () => [props.isVisible, props.initialMode, props.initialTitle, props.initialDescription, props.initialCaption, props.initialPostType] as const,
  () => {
    applyFormStateForOpenModal();
  },
  { immediate: true }
);

watch([_title, _description], () => {
  if (isApplyingQuickStart.value) {
    return;
  }
  selectedQuickStartId.value = null;
});

// Button text =================================================================
const buttonText = computed<string>(() => {
  if (mode.value === "manual") {
    return isSaving.value ? "Saving" : "Save";
  }
  return isSaving.value ? "Generating" : isManyPosts.value ? "Generate Posts" : "Generate";
});

// Manual mode helpers ========================================================
function handleMediaSelectionChanged() {
  manualValidationError.value = "";
  isFormValid.value = true;
}

function handlePhotoUploadSelect(_assetId: string) {
  handleMediaSelectionChanged();
  manualUploadsSelectedCount.value++;
}

function handlePhotoUploadDeselect(_assetId: string) {
  manualUploadsSelectedCount.value = Math.max(0, manualUploadsSelectedCount.value - 1);
}

async function handleManualAlbumSelected(contentSummary: ContentSummaryPreview) {
  manualContentSummaryPreview.value = contentSummary; // Store the preview for later
  isManualAlbumPickerOpen.value = false;
  await loadManualAlbumPayload(contentSummary);
}

async function loadManualAlbumPayload(contentSummary: ContentSummaryPreview) {
  isLoadingManualAlbum.value = true;
  try {
    manualAlbumPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromSummaryId(props.customerId, contentSummary.id, SocialShareSummaryContentType.All);
  } finally {
    isLoadingManualAlbum.value = false;
  }
}

const displayedManualAlbumAssetIds = computed(() => {
  if (!manualAlbumPayload.value) {
    return [];
  }
  return filterAlbumAssetsForPostType(manualAlbumPayload.value.assets, postType.value);
});

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save(): Promise<boolean> {
  isSaving.value = true;
  try {
    if (mode.value === "manual") {
      manualValidationError.value = "";
      const syncResult = validateManualPostInputs({
        postType: postType.value,
        assetIds: manualSelectedAssetIds.value,
        caption: _caption.value,
      });
      if (!syncResult.ok) {
        isFormValid.value = false;
        manualValidationError.value = syncResult.message;
        return false;
      }

      const metadataResult = await validateManualPostMediaMetadata({
        postType: postType.value,
        assetIds: manualSelectedAssetIds.value,
      });
      if (!metadataResult.ok) {
        isFormValid.value = false;
        manualValidationError.value = metadataResult.message;
        return false;
      }

      const response = await ContentCalendarRepo.createManualPost(props.customerId, {
        caption: isStoryPost.value ? "" : _caption.value,
        assetIds: manualSelectedAssetIds.value,
        postType: postType.value,
        weekGroup: props.weekGroup,
        contentSourceType: manualContentSummaryPreview.value?.contextType,
        sourceEntityId: manualContentSummaryPreview.value?.contextId,
        manualUploadsSelected: manualUploadsSelectedCount.value,
        locationTagSettings: locationTagSettings.value,
        physicalLocationId: physicalLocationId.value,
        instagramCollaboratorHandles: instagramCollaboratorHandles.value,
      });

      emit("createNewItemAndPost", response.newItemAndPost);
      return true;
    }

    if (mode.value === "ai") {
      if (postType.value !== SocialPostType.Post) {
        isFormValid.value = false;
        return false;
      }

      if (!_title.value || !_description.value) {
        isFormValid.value = false;
        return false;
      }

      const response = await ContentCalendarRepo.matchAndGeneratePost(props.customerId, {
        title: _title.value,
        description: _description.value,
        weekGroup: props.weekGroup,
        contentSourceType: selectedContentSummary.value?.contextType,
        sourceEntityId: selectedContentSummary.value?.contextId,
        generateManyPosts: isManyPosts.value,
      });

      emit("createCalendarItems", response.calendarItems);
      return true;
    }

    getGlobalRemoteLogger().error(`NewPostModal: Unknown mode ${mode.value} for ${props.customerId}`);
    return false;
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 3px !important;
}

.create-type-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.create-type-row__label {
  font-size: 1.5rem;
  margin-bottom: 0;
  white-space: nowrap;
}

.many-posts-instructions {
  margin: 18px 0 0;
  width: 100%;
}

.quick-starts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin-top: 16px;

  :deep(.dropdown-wrapper) {
    flex: 0 0 auto;
  }
}

.quick-starts__label {
  color: #666;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
}

.quick-starts__pill {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.2;
  padding: 5px 10px;
  white-space: nowrap;

  &:hover {
    border-color: #bbb;
  }

  &:focus-visible {
    outline: 2px solid rgba(17, 134, 137, 0.5);
    outline-offset: 1px;
  }

  &--selected {
    background: rgba(17, 134, 137, 0.06);
    border-color: #118689;
    color: #0d686b;
  }

  &--more {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding-right: 8px;

    :deep(svg) {
      fill: currentColor;
    }
  }
}

.quick-starts__more-menu {
  min-width: 210px;
  padding: 4px 0;
}

.quick-starts__more-item {
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: block;
  font-family: inherit;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.3;
  padding: 8px 12px;
  text-align: left;
  width: 100%;
  white-space: nowrap;

  &:hover {
    background: rgba(17, 134, 137, 0.06);
  }

  &--selected {
    background: rgba(17, 134, 137, 0.08);
    color: #0d686b;
  }
}

.create-type-row__ai-warning {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 8px 0 0;
}

// Photo section container ======================================================
.photo-section-container {
  padding: 12px;
  border-radius: 4px;
  transition:
    border-color 0.05s ease-in-out,
    box-shadow 0.05s ease-in-out;

  &--error {
    border: 1px rgba(178, 34, 34, 0.6) solid;
    box-shadow: 0 0 0 5px rgba(178, 34, 34, 0.2);
  }
}
</style>
